import { useState } from 'react';
import api from '@/lib/axios';
import { dialogs } from '@/stores/dialog-store';

export function PublicInvoicePage() {
  const [folio, setFolio] = useState('');
  const [total, setTotal] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerRfc, setCustomerRfc] = useState('');
  const [taxRegime, setTaxRegime] = useState('616');
  const [taxZipCode, setTaxZipCode] = useState('');
  const [cfdiUse, setCfdiUse] = useState('G03');
  const [email, setEmail] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [successData, setSuccessData] = useState<{uuid: string, pdf: string, xml: string} | null>(null);

  const handleInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!folio || !total || !customerName || !customerRfc || !taxRegime || !taxZipCode || !cfdiUse) {
      dialogs.alert('Por favor completa todos los campos requeridos.', 'Faltan datos');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/public/invoices/auto-stamp', {
        ticketFolio: folio,
        totalAmount: Number(total),
        customerName,
        customerRfc,
        taxRegime,
        taxZipCode,
        cfdiUse,
        email
      }, { headers: { 'x-bypass-local': 'true' } }); // In case it's served via Electron

      setSuccessData({
        uuid: response.data.uuid,
        pdf: response.data.pdfUrl,
        xml: response.data.xmlUrl,
      });

      dialogs.alert('Tu factura ha sido timbrada exitosamente.', '¡Factura Generada!');
    } catch (error: any) {
      dialogs.alert(error.response?.data?.message || 'Ocurrió un error inesperado al procesar tu factura.', 'Error al facturar');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (url: string) => {
    window.open(url, '_blank');
  };

  if (successData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md border-t-4 border-t-emerald-500 overflow-hidden">
          <div className="p-6 text-center border-b border-gray-100">
            <h2 className="text-2xl font-bold text-emerald-600 mb-2">¡Factura Exitosa!</h2>
            <p className="text-sm text-gray-500 font-mono bg-gray-50 p-2 rounded">Folio: {successData.uuid}</p>
          </div>
          
          <div className="p-6 space-y-4">
            <p className="text-sm text-gray-600 text-center">
              Tu factura ha sido generada correctamente. Puedes descargar tus archivos a continuación.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <button 
                onClick={() => handleDownload(successData.pdf)} 
                className="py-2 px-4 border border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg font-medium transition-colors"
              >
                Descargar PDF
              </button>
              <button 
                onClick={() => handleDownload(successData.xml)} 
                className="py-2 px-4 border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg font-medium transition-colors"
              >
                Descargar XML
              </button>
            </div>
          </div>
          
          <div className="p-4 bg-gray-50 border-t border-gray-100">
            <button 
              className="w-full py-2 text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors" 
              onClick={() => setSuccessData(null)}
            >
              Facturar otro ticket
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg border-t-4 border-t-blue-500 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Autofacturación</h2>
          <p className="text-sm text-gray-500">
            Ingresa los datos de tu ticket y tus datos fiscales para generar tu factura electrónica (CFDI 4.0).
          </p>
        </div>
        
        <div className="p-6">
          <form onSubmit={handleInvoice} className="space-y-6">
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 border-b pb-2">Datos del Ticket</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label htmlFor="folio" className="text-xs font-bold text-gray-700 uppercase">Folio del Ticket *</label>
                  <input id="folio" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" placeholder="Ej. T-0001" value={folio} onChange={e => setFolio(e.target.value)} required />
                </div>
                <div className="space-y-1">
                  <label htmlFor="total" className="text-xs font-bold text-gray-700 uppercase">Total a Pagar *</label>
                  <input id="total" type="number" step="0.01" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" placeholder="Ej. 150.50" value={total} onChange={e => setTotal(e.target.value)} required />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 border-b pb-2">Datos Fiscales</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label htmlFor="rfc" className="text-xs font-bold text-gray-700 uppercase">RFC *</label>
                  <input id="rfc" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none uppercase" placeholder="XAXX010101000" value={customerRfc} onChange={e => setCustomerRfc(e.target.value.toUpperCase())} required />
                </div>
                <div className="space-y-1">
                  <label htmlFor="cp" className="text-xs font-bold text-gray-700 uppercase">Código Postal *</label>
                  <input id="cp" maxLength={5} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" placeholder="12345" value={taxZipCode} onChange={e => setTaxZipCode(e.target.value)} required />
                </div>
              </div>
              <div className="space-y-1">
                <label htmlFor="nombre" className="text-xs font-bold text-gray-700 uppercase">Razón Social o Nombre *</label>
                <input id="nombre" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none uppercase" placeholder="JUAN PEREZ" value={customerName} onChange={e => setCustomerName(e.target.value.toUpperCase())} required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label htmlFor="regimen" className="text-xs font-bold text-gray-700 uppercase">Régimen Fiscal *</label>
                  <select id="regimen" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white text-sm" value={taxRegime} onChange={e => setTaxRegime(e.target.value)}>
                    <option value="601">601 - General de Ley Personas Morales</option>
                    <option value="612">612 - Personas Físicas con Actividades Empresariales</option>
                    <option value="616">616 - Sin obligaciones fiscales</option>
                    <option value="626">626 - RESICO</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label htmlFor="uso" className="text-xs font-bold text-gray-700 uppercase">Uso CFDI *</label>
                  <select id="uso" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white text-sm" value={cfdiUse} onChange={e => setCfdiUse(e.target.value)}>
                    <option value="G01">G01 - Adquisición de mercancías</option>
                    <option value="G03">G03 - Gastos en general</option>
                    <option value="S01">S01 - Sin efectos fiscales</option>
                  </select>
                </div>
              </div>
              <div className="space-y-1">
                <label htmlFor="email" className="text-xs font-bold text-gray-700 uppercase">Correo (Opcional)</label>
                <input id="email" type="email" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" placeholder="correo@ejemplo.com" value={email} onChange={e => setEmail(e.target.value)} />
              </div>
            </div>

            <button 
              type="submit" 
              className={`w-full py-4 rounded-xl font-black text-lg uppercase tracking-widest text-white transition-all ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 hover:shadow-lg'}`}
              disabled={loading}
            >
              {loading ? 'Procesando...' : 'Generar Factura'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
